import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useRouteError } from 'react-router-dom';

/**
 * Una pantalla que truena no deja la aplicacion en blanco.
 *
 * El caso mas comun no es un error de programacion sino una version nueva
 * publicada mientras el consultorio tenia la pestana abierta: el trozo de
 * codigo que pide la pantalla ya no existe en el servidor. Recargar lo
 * resuelve, y eso es lo que se ofrece primero.
 */
function PantallaDeFallo({ error, alReintentar }: { error: unknown; alReintentar?: () => void }) {
  const texto = error instanceof Error ? error.message : '';
  const versionNueva = /dynamically imported module|Loading chunk|Importing a module script/i.test(
    texto,
  );

  return (
    <Box role="alert" sx={{ maxWidth: 560, mx: 'auto', my: 'var(--space-10)', px: '16px' }}>
      <Stack
        sx={{
          gap: 'var(--space-4)',
          p: 'var(--space-7)',
          bgcolor: 'var(--bg-raised)',
          border: '1px solid var(--border-card)',
          borderRadius: 'var(--radius-frame)',
        }}
      >
        <ReportProblemRoundedIcon aria-hidden sx={{ color: 'var(--status-warning-text)' }} />
        <Typography variant="h2" component="h1">
          {versionNueva ? 'Hay una versión nueva' : 'Esta pantalla no pudo mostrarse'}
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)' }}>
          {versionNueva
            ? 'La aplicación se actualizó mientras la tenía abierta. Recargue para continuar.'
            : 'Lo que ya estaba guardado no se perdió. Recargue la página; si vuelve a pasar, avise a soporte con la hora en que ocurrió.'}
        </Typography>
        <Stack
          direction="row"
          useFlexGap
          flexWrap="wrap"
          sx={{ gap: 'var(--space-3)', mt: 'var(--space-2)' }}
        >
          <Button
            variant="contained"
            startIcon={<ReplayRoundedIcon />}
            onClick={() => window.location.reload()}
          >
            Recargar
          </Button>
          {alReintentar && !versionNueva && (
            <Button variant="outlined" onClick={alReintentar}>
              Intentar sin recargar
            </Button>
          )}
          <Button variant="text" color="inherit" href="/">
            Ir al inicio
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

/** Para el `errorElement` del enrutador: lo que truena fuera del armazon. */
export function FronteraDeError() {
  const error = useRouteError();
  return <PantallaDeFallo error={error} />;
}

/**
 * Frontera DENTRO del armazon: si truena una pantalla, la navegacion sigue en
 * su sitio. `llave` (la ruta) la reinicia al cambiar de pantalla.
 */
export class FronteraDeContenido extends Component<
  { children: ReactNode; llave: string },
  { error: unknown }
> {
  override state = { error: null as unknown };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override componentDidUpdate(previas: { llave: string }) {
    if (previas.llave !== this.props.llave && this.state.error) this.setState({ error: null });
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    // Sin datos de la paciente: solo el error y la pila de componentes.
    console.error('Fallo al mostrar la pantalla', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <PantallaDeFallo
          error={this.state.error}
          alReintentar={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}
